import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/atoms/Dialog";
import { Button } from "@/components/atoms/Button";
import { ZoomIn } from "lucide-react";

const OUTPUT_SIZE = 512; // px — mesma resolução final pra qualquer foto de origem
const CANVAS_SIZE = 280; // área de edição visível na tela (menor que a saída, só exibição)

interface PhotoCropDialogProps {
  file: File | null;
  onCancel: () => void;
  onCropped: (blob: Blob) => void;
}

/**
 * Recorte de foto de perfil — canvas nativo do navegador, sem biblioteca
 * externa nova (evita depender de um pacote que eu não consigo instalar
 * nem testar neste ambiente). Fluxo: usuário escolhe o arquivo → abre este
 * diálogo → arrasta pra reposicionar + controla o zoom → "Cortar" gera um
 * PNG quadrado final e devolve pro chamador (que faz o upload de verdade,
 * sem nenhuma mudança no StorageService).
 */
export function PhotoCropDialog({ file, onCancel, onCropped }: PhotoCropDialogProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [minZoom, setMinZoom] = useState(1);
  const dragState = useRef<{ startX: number; startY: number; startOffsetX: number; startOffsetY: number } | null>(null);

  // Carrega a imagem escolhida sempre que o arquivo mudar; zoom mínimo =
  // o necessário pra cobrir o quadrado inteiro (nunca deixa sobrar borda vazia).
  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      imageRef.current = img;
      const coverZoom = CANVAS_SIZE / Math.min(img.width, img.height);
      setMinZoom(coverZoom);
      setZoom(coverZoom);
      setOffset({ x: 0, y: 0 });
    };
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = CANVAS_SIZE;
    canvas.height = CANVAS_SIZE;
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    const w = img.width * zoom;
    const h = img.height * zoom;
    const x = (CANVAS_SIZE - w) / 2 + offset.x;
    const y = (CANVAS_SIZE - h) / 2 + offset.y;
    ctx.drawImage(img, x, y, w, h);

    // Máscara circular por cima — só de guia visual, o corte final é
    // quadrado (avatar circular na tela, arquivo quadrado no storage,
    // mesmo padrão de qualquer app).
    ctx.save();
    ctx.globalCompositeOperation = "destination-in";
    ctx.beginPath();
    ctx.arc(CANVAS_SIZE / 2, CANVAS_SIZE / 2, CANVAS_SIZE / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }, [zoom, offset]);

  useEffect(() => {
    draw();
  }, [draw]);

  // Trava o deslocamento pra nunca sobrar borda vazia dentro do círculo,
  // qualquer que seja o zoom atual.
  function clampOffset(next: { x: number; y: number }, currentZoom: number) {
    const img = imageRef.current;
    if (!img) return next;
    const w = img.width * currentZoom;
    const h = img.height * currentZoom;
    const maxX = Math.max(0, (w - CANVAS_SIZE) / 2);
    const maxY = Math.max(0, (h - CANVAS_SIZE) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, next.x)),
      y: Math.min(maxY, Math.max(-maxY, next.y)),
    };
  }

  function handlePointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
    dragState.current = { startX: e.clientX, startY: e.clientY, startOffsetX: offset.x, startOffsetY: offset.y };
  }

  function handlePointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!dragState.current) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    setOffset(clampOffset({ x: dragState.current.startOffsetX + dx, y: dragState.current.startOffsetY + dy }, zoom));
  }

  function handlePointerUp() {
    dragState.current = null;
  }

  function handleZoomChange(value: number) {
    setZoom(value);
    setOffset((prev) => clampOffset(prev, value));
  }

  function handleCrop() {
    const img = imageRef.current;
    if (!img) return;
    const output = document.createElement("canvas");
    output.width = OUTPUT_SIZE;
    output.height = OUTPUT_SIZE;
    const ctx = output.getContext("2d");
    if (!ctx) return;

    const scale = OUTPUT_SIZE / CANVAS_SIZE;
    const w = img.width * zoom * scale;
    const h = img.height * zoom * scale;
    const x = (OUTPUT_SIZE - w) / 2 + offset.x * scale;
    const y = (OUTPUT_SIZE - h) / 2 + offset.y * scale;
    ctx.drawImage(img, x, y, w, h);

    output.toBlob(
      (blob) => {
        if (blob) onCropped(blob);
      },
      "image/jpeg",
      0.9
    );
  }

  return (
    <Dialog open={Boolean(file)} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ajustar foto</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4">
          <canvas
            ref={canvasRef}
            width={CANVAS_SIZE}
            height={CANVAS_SIZE}
            className="cursor-move touch-none rounded-full border border-border bg-muted"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
          />
          <div className="flex w-full items-center gap-3">
            <ZoomIn className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              type="range"
              min={minZoom}
              max={minZoom * 3}
              step={0.01}
              value={zoom}
              onChange={(e) => handleZoomChange(Number(e.target.value))}
              className="h-2 w-full cursor-pointer appearance-none rounded-full bg-muted accent-primary"
              aria-label="Zoom da foto"
            />
          </div>
          <p className="text-center text-xs text-muted-foreground">Arraste a foto pra reposicionar e use o controle pra ajustar o zoom.</p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} className="flex-1">
            Cancelar
          </Button>
          <Button onClick={handleCrop} className="flex-1">
            Cortar e salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
